import { notification, Upload, UploadProps } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { AntdFormItem, AntdFormItemProps } from '@/common/components/AntdForm/AntdFormItem.tsx'
import { UploadOutlined } from '@ant-design/icons'
import { useUploadEmployeeDocumentMutation } from '@/features/employee-data/employee-data.api.ts'
import { handleApiError } from '@/utils/api.tsx'
import { HcFile } from '@/common/types/personal-info.def.ts'
import { useUploadOnboardingDocumentMutation } from '@/features/onboarding/onboarding.api.ts'
import { UploadEmployeeDocumentRequest } from '@/features/employee-data/employee-data-request.def.ts'
import { useDocumentStorageConfig } from '@/pages/employee-data/hooks/useDocumentStorageConfig.tsx'


export type UploadInputBaseProps = {
  documentBucket?: 'employee' | 'onboarding'
  employeeId: string
  documentType: string
  disabled?: boolean
}

type MultiUploadInputProps = UploadInputBaseProps & {
  value?: HcFile[]
  onChange?: (value: HcFile[]) => void
}

export const HcFilesUpload: React.FC<MultiUploadInputProps> = ({
  value = [],
  onChange,
  disabled,
  documentType,
  documentBucket = 'employee',
  employeeId,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadEmployeeDocument] = useUploadEmployeeDocumentMutation()
  const [uploadOnboardingDocument] = useUploadOnboardingDocumentMutation()

  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // Names currently being uploaded within the same batch. Prevents concurrent
  // onChange calls (multiple files dropped at once) from both passing the
  // duplicate-name check before either has been appended to `value`.
  const uploadingNamesRef = useRef<Set<string>>(new Set())

  const onFileAppend = (doc: HcFile) => {
    const next = [...(valueRef.current ?? []), doc]
    valueRef.current = next
    onChange?.(next)
  }

  const uploadButtonProps: UploadProps = {
    fileList: value ? value.map(x => ({
      name: x.fileName,
      uid: x.fileId,
    })) : [],
    showUploadList: true,
    multiple: true,
    beforeUpload: () => false,
    async onChange(info) {
      if (info.file.status === 'removed') {
        const next = valueRef.current.filter(x => x.fileId !== info.file.uid)
        valueRef.current = next
        onChange?.(next)
      } else if (info.file) {
        const fileName = info.file.name
        if (valueRef.current.some(x => x.fileName === fileName) || uploadingNamesRef.current.has(fileName)) {
          notification.error({
            message: 'File with the same name exist already',
          })
          return
        }
        try {
          if (!employeeId) return

          uploadingNamesRef.current.add(fileName)
          setUploading(true)
          const request: UploadEmployeeDocumentRequest = {
            employeeId: employeeId,
            type: documentType,
            document: info.file as unknown as File,
          }

          const data = documentBucket === 'onboarding'
            ? await uploadOnboardingDocument(request).unwrap()
            : await uploadEmployeeDocument(request).unwrap()

          onFileAppend(data)

        } catch (e) {
          handleApiError(e, 'Failed to upload')
        } finally {
          uploadingNamesRef.current.delete(fileName)
          setUploading(false)
        }
      }
    },
  }

  return (
    <Upload.Dragger {...uploadButtonProps} disabled={disabled || uploading}>
      <UploadOutlined /> Click here or drag files
    </Upload.Dragger>
  )
}

export const AntdFormEmployeeDocumentUpload: React.FC<MultiUploadInputProps & AntdFormItemProps> = ({
  fieldProps,
  ...props
}) => {
  const { documentBucket } = useDocumentStorageConfig()
  const uploadDocumentBucket = documentBucket === 'workable' ? undefined : documentBucket

  return (
    <AntdFormItem {...fieldProps}>
      <HcFilesUpload {...props} documentBucket={uploadDocumentBucket} />
    </AntdFormItem>
  )
}

type SingleUploadInputProps = UploadInputBaseProps & {
  value?: HcFile
  onChange?: (value?: HcFile) => void
}

export const SingleHcFilesUpload: React.FC<SingleUploadInputProps> = ({
  value = undefined,
  onChange,
  disabled,
  documentType,
  documentBucket = 'employee',
  employeeId,
}) => {
  const [uploading, setUploading] = useState(false)
  const [uploadEmployeeDocument] = useUploadEmployeeDocumentMutation()
  const [uploadOnboardingDocument] = useUploadOnboardingDocumentMutation()

  const uploadButtonProps: UploadProps = {
    fileList: value ? [{
      name: value.fileName,
      uid: value.fileId,
    }] : [],
    showUploadList: true,
    beforeUpload: () => false,
    async onChange(info) {
      if (info.file.status === 'removed') {
        onChange?.(undefined)
      } else if (info.file) {
        try {
          if (!employeeId) return

          setUploading(true)
          const request: UploadEmployeeDocumentRequest = {
            employeeId: employeeId,
            type: documentType,
            document: info.file as unknown as File,
          }

          const data = documentBucket === 'onboarding'
            ? await uploadOnboardingDocument(request).unwrap()
            : await uploadEmployeeDocument(request).unwrap()

          onChange?.(data)

        } catch (e) {
          handleApiError(e, 'Failed to upload')
        } finally {
          setUploading(false)
        }
      }
    },
  }

  return (
    <Upload.Dragger {...uploadButtonProps} disabled={disabled || uploading}>
      <UploadOutlined /> Click here or drag single file
    </Upload.Dragger>
  )
}

export const AntdFormSingleEmployeeDocumentUpload: React.FC<SingleUploadInputProps & AntdFormItemProps> = ({
  fieldProps,
  ...props
}) => {
  const { documentBucket } = useDocumentStorageConfig()
  const uploadDocumentBucket = documentBucket === 'workable' ? undefined : documentBucket

  return (
    <AntdFormItem {...fieldProps}>
      <SingleHcFilesUpload {...props} documentBucket={uploadDocumentBucket} />
    </AntdFormItem>
  )
}